<template>
	<div class="app-header" :class="{ 'app-header-vertical':verticalHeader }">
		<div class="app-header-left">
			<template v-if="hasBurgerMenu">
				<anchor-element :tag="SymIcon" :chain="menuChain" @click="menuChain.toggle" name="menu-18" class="menu-opener"></anchor-element>
				<boat-element :tag="SideBar" :chain="menuChain" :drop-menu="true" :history="false" :menu="navStore.sideMenu"></boat-element>
				<template v-if="navStore.hasHistory">
					<anchor-element :tag="SymIcon" :chain="historyChain" @click="historyChain.toggle" name="history" class="menu-opener"></anchor-element>
					<boat-element :tag="HistoryMenu" :chain="historyChain"></boat-element>
				</template>
			</template>
			<app-logo v-if="logoDisplay !== 'no'" :title="file" :reduce="logoDisplay === 'reduced'"></app-logo>
		</div>
		<div class="app-header-center" v-if="search"><div id="searchContainer" style="width:100%"><search-widget></search-widget></div></div>
		<div class="app-header-right">
			<notification-bar v-if="notification" @measure="notificationBarChanged" ref="bar"></notification-bar>
			<theme-selector v-if="theme"></theme-selector>
			<template v-if="env.user">
				<anchor-element tag="img" :chain="chain" :src="env.user.avatar" class="avatar" :title="env.user.name" @click="chain.toggle"></anchor-element>
				<boat-element :tag="MenuPanel" :chain="chain" :menu="userMenu"></boat-element>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import ThemeSelector from "@/ui/ThemeSelector.vue";
import SearchWidget from "@/search/SearchWidget.vue";
import {injectEnv, userInRole} from "@fd/Environment";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree";
import MenuPanel from "@fd/editors/menu/MenuPanel.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import type {Menu, NavigateItem, SeparatorItem, SideMenu} from "@t/ui/Menu";
import {computed, onMounted, ref, watch} from "vue";
import NotificationBar from "@/ui/mouse/NotificationBar.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {injectLayoutWidth} from "@fd/LayoutWidth.ts";
import AppLogo from "@/ui/mouse/AppLogo.vue";
import SideBar from "@/ui/mouse/SideBar.vue";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {SimplifiedUser} from "@t/user/SimplifiedUser.ts";
import HistoryMenu from "@/ui/mouse/HistoryMenu.vue";

const props = withDefaults(defineProps<{
	search?: boolean,
	notification?: boolean,
	theme?: boolean,
	file?: string,
}>(), {
	search: false,
	notification: false,
	theme: true,
	file: ""
});

const env = injectEnv();
const navStore = useNavigationStore();
const bar = ref(null);
const width = injectLayoutWidth();
const AVATAR_WIDTH = 32;
const THEME_SELECTOR_WIDTH = 26;
const SEARCH_WIDGET_WIDTH = 400;	// standard width
const FULL_LOGO_WIDTH = 163;		// intouch crm
const REDUCED_LOGO_WIDTH = 50;		// itcrm
const maxSpace = computed<number>(() => width.value - 32);	// screen width minus 16px padding on the left/right
const notificationBarWidth = ref<number>(0);

const rightControlsWidth = computed<number>(() => {
	let components = 0;
	let w = 0;
	if (env.user) {
		components++;
		w += AVATAR_WIDTH;
	}
	if (props.theme) {
		components++;
		w += THEME_SELECTOR_WIDTH;
	}
	if (props.notification) {
		components++;
		w += notificationBarWidth.value;
	}
	if (components >= 2) {
		w += (components - 1) * 16;	// 16 is gap between components
	}
	return w;
});

const BURGER_MENU_WIDTH = 18;
const HISTORY_MENU_WIDTH = 18;

const verticalHeader = computed<boolean>(() => {
	let weNeed: number = 0;
	if (width.value < 1024) weNeed += BURGER_MENU_WIDTH + 16;	// burger menu + gap
	weNeed += FULL_LOGO_WIDTH;
	if (props.search) weNeed += 16 + SEARCH_WIDGET_WIDTH;	// widget plus gap
	weNeed += 16 + rightControlsWidth.value;
	return weNeed > maxSpace.value;
});


const logoDisplay = computed<"full"|"reduced"|"no">(() => {
	if (!verticalHeader.value) return "full";
	const historyMenuSize = navStore.hasHistory ? (HISTORY_MENU_WIDTH + 16) : 0;
	const needForFullLogo = BURGER_MENU_WIDTH + 16 + historyMenuSize + FULL_LOGO_WIDTH + 16 + rightControlsWidth.value;
	const needForReducedLogo = BURGER_MENU_WIDTH + 16 + historyMenuSize + REDUCED_LOGO_WIDTH + 16 + rightControlsWidth.value;
	if (maxSpace.value >= needForFullLogo) return "full";
	if (maxSpace.value >= needForReducedLogo) return "reduced";
	return "no";
});

const chain = createChain({
	rule: {
		ah: 100, av: 100,
		bh: 100, bv: 0,
		x: 0, y: 0,
		tail: "top-right"
	}
}, "user-menu");

const menuChain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: -8, y: 4,
		tail: "top-left"
	}
}, "side-menu");

const historyChain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: -8, y: 4,
		tail: "top-left"
	}
}, "history-menu");

watch(width, () => {
	menuChain.close();
	historyChain.close();
});
function notificationBarChanged():void {
	if (bar.value) notificationBarWidth.value = bar.value.getWidth();
}

const profileRoute = computed<string>(() => {
	if (env.user) {
		if (env.user.access === "PARTY") {
			return "customerFront.do?id="+encodeURIComponent(env.user.hash);
		} else {
			return "editUser.do?id="+env.user.id;
		}
	}
	return "#"
});

const logoutRoute = computed<string>(() => {
	if (env.user) {
		if (env.user.access === "PARTY") {
			return "extranetLogin.do";
		} else {
			return "presence.do?newLogin=true";
		}
	}
	return "#"
});

const hasBurgerMenu = computed<boolean>(() => navStore.sideMenu != null && Array.isArray(navStore.sideMenu.groups) && navStore.sideMenu.groups.length > 0);

const otherActiveUsers = ref<SimplifiedUser[]>([]);

function detectOtherUsers() {
	env.users().then((allUsers) => {
		for (let u of allUsers) {
			if (u.active && (u.access === "STANDARD" || u.access === "OPERATOR") && u.id !== env.user.id) {
				otherActiveUsers.value.push(u);
			}
		}
		// sort by name
		otherActiveUsers.value.sort((u1, u2) => {
			return u1.name.localeCompare(u2.name);
		});
	});
}

const userMenu = computed<Menu>(() => {
	const m:Menu = {
		type: "menu",
		children: []
	};
	if(userInRole(env.user, "ui.avatar.editProfile")) {
		m.children.push(<NavigateItem>{
			type: "nav",
			icon: "user",
			title: env.user.name,
			link: profileRoute.value
		});
	} else {
		m.children.push(<HtmlItem>{
			type: "html",
			icon: "user",
			body: env.user.name
		});
	}
	
	m.children.push(<NavigateItem>{
		type: "nav",
		icon: "what",
		title: env.msg('help.help'),
		link: "https://doc.intouch-crm.cz",
		target: "_blank"
	});
	m.children.push(<NavigateItem>{
		type: "nav",
		icon: "logout",
		title: env.msg('menu.logout'),
		link: logoutRoute.value
	});
	if ((env.user.master || userInRole(env.user, "admin.app")) && otherActiveUsers.value.length > 0) {
		m.children.push(<SeparatorItem>{ type: "separator" });
		const um = <Menu>{
			type: "menu",
			icon: "user-group-2",
			title: env.msg("log.in.as"),
			parentAnchor: "left-top",
			childAnchor: "right-top",
			children:[]
		}
		for (let ou of otherActiveUsers.value) {
			um.children.push(<NavigateItem>{
				type: "nav",
				title: ou.name,
				link: "su.do?id="+ou.id
			});
		}
		m.children.push(um);
	}
	return m;
});

onMounted(() => {
	notificationBarChanged();
	detectOtherUsers();
});
</script>

<style>
.app-header {
	box-sizing: border-box;
	width: 100%;
	display: grid;
	grid-template-columns: auto auto auto;
	padding: 16px;
	align-items: center;
	justify-content: space-between;
	column-gap: 16px;
}

.app-header-left,
.app-header-center,
.app-header-right {
	display: flex;
	flex-direction: row;
	column-gap: 16px;
	box-sizing: content-box;
	height: 32px;
	align-self: start;
}

.app-header-left {
	grid-column: 1 / 2;
	grid-row: 1 / 2;
	align-items: flex-start;
	justify-content: flex-start;
}

.app-header-center {
	grid-column: 2 / 3;
	grid-row: 1 / 2;
	justify-content: center;
	align-items: center;
}

.app-header-right {
	grid-column: 3 / 4;
	grid-row: 1 / 2;
	justify-content: flex-end;
	align-items: center;
}


.app-header.app-header-vertical {
	grid-template-columns: auto auto;
	row-gap: 8px;

	& > .app-header-left {
		grid-column: 1 / 2;
		grid-row: 1 / 2;
	}

	& > .app-header-center {
		margin-left: -3px; /* visual compensation for round border */
		grid-column: 1 / 3;
		grid-row: 2 / 3;
	}

	& > .app-header-right {
		grid-column: 2 / 3;
		grid-row: 1 / 2;
	}
}


.fi.menu-opener {
	margin-top: 6px;
	cursor: pointer;
	display: none;
}

</style>
